import axios, { AxiosRequestConfig, AxiosError } from 'axios';
import { SUPER_ADMIN_PATH } from '../config/secret';
import { captureError } from '../lib/sentry';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,   // sends httpOnly jwt cookie on every request
});

/* ── Token Refresh State ─────────────────────────────────────── */
let isRefreshing = false;
// Queue of { resolve, reject } callbacks waiting for the refresh to finish
let refreshQueue: Array<{ resolve: () => void; reject: (err: unknown) => void }> = [];

function processQueue(err: unknown) {
  refreshQueue.forEach(({ resolve, reject }) => (err ? reject(err) : resolve()));
  refreshQueue = [];
}

async function attemptRefresh(): Promise<void> {
  // Call refresh without going through axiosInstance (avoids interceptor recursion)
  await axios.post(`${API_BASE_URL}/auth/refresh`, {}, { withCredentials: true });
}

/* ── Session-expired redirect (only after refresh fails) ─────── */
let sessionExpiredPending = false;

function redirectToLogin() {
  if (sessionExpiredPending) return;
  sessionExpiredPending = true;

  window.dispatchEvent(new CustomEvent('session-expired', { detail: { redirectIn: 3000 } }));

  const isSuperAdmin = window.location.pathname.startsWith(`/${SUPER_ADMIN_PATH}`);
  const base = isSuperAdmin ? `/${SUPER_ADMIN_PATH}` : '/login';

  setTimeout(() => {
    sessionExpiredPending = false;
    window.location.href = `${base}?session_expired=1`;
  }, 3000);
}

/* ── Skip-list: never attempt refresh for these paths ───────── */
const SKIP_REFRESH_PATHS = [
  '/auth/login',
  '/auth/refresh',
  '/auth/register',
  '/auth/forgot-password',
  '/auth/verify',
  '/auth/reset-password',
];

function shouldSkipRefresh(url: string): boolean {
  const currentPath = window.location.pathname;
  const skipPagePaths = [
    `/${SUPER_ADMIN_PATH}`,
    '/login',
    '/register',
    '/forgot-password',
    '/track/',
    '/feedback/',
    '/unauthorized',
  ];
  return (
    SKIP_REFRESH_PATHS.some(p => url.includes(p)) ||
    skipPagePaths.some(p => currentPath.startsWith(p))
  );
}

/* ── Response interceptor ─────────────────────────────────────── */
axiosInstance.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status !== 401 || originalRequest._retry) {
      // Report non-auth errors to Sentry (captureError is a no-op if DSN isn't configured)
      const status = error.response?.status;
      if (status && status !== 401 && status !== 403) {
        captureError(error, {
          url: originalRequest.url,
          method: originalRequest.method,
          status,
        });
      }
      return Promise.reject(error);
    }

    const url = originalRequest.url ?? '';
    if (shouldSkipRefresh(url)) {
      return Promise.reject(error);
    }

    // If a refresh is already in progress, queue this request
    if (isRefreshing) {
      return new Promise<void>((resolve, reject) => {
        refreshQueue.push({ resolve, reject });
      }).then(() => axiosInstance(originalRequest))
        .catch(err => Promise.reject(err));
    }

    // First 401 — attempt refresh
    originalRequest._retry = true;
    isRefreshing = true;

    try {
      await attemptRefresh();
      processQueue(null);    // let all queued requests through
      isRefreshing = false;
      return axiosInstance(originalRequest);   // replay the original request
    } catch (refreshError) {
      processQueue(refreshError);   // reject all queued requests
      isRefreshing = false;
      redirectToLogin();            // refresh itself failed → kick user out
      return Promise.reject(refreshError);
    }
  }
);

export default axiosInstance;
